import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import { 
  FileText, Upload, Search, Filter, Download, Trash2, Eye,
  FolderOpen, File, FileImage, FileSpreadsheet, Loader2
} from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';

const AdminDocumentsPage = () => {
  const { user } = useAuth();
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [uploading, setUploading] = useState(false);
  const [uploadCategory, setUploadCategory] = useState('other');
  const fileInputRef = useRef(null);

  const categories = [
    { value: 'all', label: 'Todas las categorías' },
    { value: 'legal', label: 'Legal' },
    { value: 'investor', label: 'Inversionistas' },
    { value: 'financial', label: 'Financiero' },
    { value: 'compliance', label: 'Cumplimiento' },
    { value: 'internal', label: 'Interno' },
    { value: 'marketing', label: 'Marketing' },
    { value: 'contract', label: 'Contratos' },
    { value: 'kyc', label: 'KYC' },
    { value: 'other', label: 'Otro' },
  ];

  const categoryLabels = Object.fromEntries(categories.map(c => [c.value, c.label]));

  const categoryColors = {
    legal: 'bg-purple-500/10 text-purple-500',
    investor: 'bg-blue-500/10 text-blue-500',
    financial: 'bg-green-500/10 text-green-500',
    compliance: 'bg-orange-500/10 text-orange-500',
    internal: 'bg-gray-500/10 text-gray-500',
    marketing: 'bg-pink-500/10 text-pink-500',
    contract: 'bg-indigo-500/10 text-indigo-500',
    kyc: 'bg-yellow-500/10 text-yellow-600',
    other: 'bg-muted text-muted-foreground',
  };

  useEffect(() => { fetchDocuments(); }, []);

  const fetchDocuments = async () => {
    setLoading(true);
    try {
      const params = {};
      if (categoryFilter !== 'all') params.category = categoryFilter;
      const res = await api.get('/admin/documents', { params });
      setDocuments(res.data.documents || []);
    } catch (err) {
      toast.error('Error al cargar documentos');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchDocuments(); }, [categoryFilter]);

  const getFileIcon = (contentType, filename) => {
    const ext = filename?.split('.').pop()?.toLowerCase();
    if (contentType?.includes('pdf') || ext === 'pdf')
      return <FileText className="h-8 w-8 text-red-500" strokeWidth={1.5} />;
    if (contentType?.includes('sheet') || ext === 'xlsx' || ext === 'xls' || ext === 'csv')
      return <FileSpreadsheet className="h-8 w-8 text-green-500" strokeWidth={1.5} />;
    if (contentType?.includes('image') || ['png','jpg','jpeg','webp'].includes(ext))
      return <FileImage className="h-8 w-8 text-blue-500" strokeWidth={1.5} />;
    return <File className="h-8 w-8 text-muted-foreground" strokeWidth={1.5} />;
  };

  const formatSize = (bytes) => {
    if (!bytes) return '--';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024*1024) return `${(bytes/1024).toFixed(1)} KB`;
    return `${(bytes/(1024*1024)).toFixed(1)} MB`;
  };

  const formatDate = (isoStr) => {
    if (!isoStr) return '--';
    try { return new Date(isoStr).toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' }); }
    catch { return isoStr.slice(0, 10); }
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileSelected = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    setUploading(true);

    for (const file of files) {
      try {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('category', uploadCategory);
        formData.append('description', file.name);
        formData.append('access_level', 'admin');

        const res = await api.post('/admin/documents/upload', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        toast.success(`${file.name} subido correctamente`);
      } catch (err) {
        const detail = err.response?.data?.detail || `Error al subir ${file.name}`;
        toast.error(detail);
      }
    }

    setUploading(false);
    e.target.value = '';
    fetchDocuments();
  };

  const handleDownload = async (doc) => {
    try {
      const res = await api.get(`/admin/documents/${doc.document_id}/download`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', doc.original_filename || 'documento');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Descarga iniciada');
      // Update local downloads count
      setDocuments(prev => prev.map(d => 
        d.document_id === doc.document_id ? { ...d, downloads: (d.downloads || 0) + 1 } : d
      ));
    } catch (err) {
      toast.error('Error al descargar el documento');
    }
  };

  const handlePreview = (doc) => {
    const storagePath = doc.storage_path || '';
    if (storagePath.startsWith('/uploads/')) {
      window.open(storagePath, '_blank');
    } else {
      handleDownload(doc);
    }
  };

  const handleDelete = async (doc) => {
    if (!confirm(`¿Eliminar "${doc.original_filename}"? Esta acción no se puede deshacer.`)) return;
    try {
      await api.delete(`/admin/documents/${doc.document_id}`);
      toast.success('Documento eliminado');
      setDocuments(prev => prev.filter(d => d.document_id !== doc.document_id));
    } catch (err) {
      toast.error('Error al eliminar el documento');
    }
  };

  const filteredDocuments = documents.filter(doc => {
    const name = (doc.original_filename || doc.description || '').toLowerCase();
    return name.includes(searchTerm.toLowerCase());
  });

  const totalSize = documents.reduce((acc, d) => acc + (d.size || 0), 0);
  const totalDownloads = documents.reduce((acc, d) => acc + (d.downloads || 0), 0);

  return (
    <div className="p-4 lg:p-8" data-testid="admin-documents-page">
      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        multiple
        className="hidden"
        onChange={handleFileSelected}
        accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.png,.jpg,.jpeg,.webp,.csv,.txt"
      />

      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="text-xl lg:text-2xl font-light tracking-tight">Gestión de Documentos</h1>
          <p className="text-muted-foreground mt-1 text-sm">Administra todos los documentos del fondo</p>
        </div>
        <div className="flex items-center gap-2 w-full sm:w-auto">
          <Select value={uploadCategory} onValueChange={setUploadCategory}>
            <SelectTrigger className="w-[130px]" data-testid="upload-category-select">
              <SelectValue placeholder="Categoría" />
            </SelectTrigger>
            <SelectContent>
              {categories.filter(c => c.value !== 'all').map(cat => (
                <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button onClick={handleUploadClick} disabled={uploading} className="gap-2 whitespace-nowrap" data-testid="upload-document-btn">
            {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
            <span className="hidden sm:inline">{uploading ? 'Subiendo...' : 'Subir Documento'}</span>
            <span className="sm:hidden">{uploading ? '...' : 'Subir'}</span>
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
        <div className="card-premium p-4">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
              <FileText className="h-5 w-5 text-primary" strokeWidth={1.5} />
            </div>
            <div>
              <p className="text-2xl font-light" data-testid="total-docs">{documents.length}</p>
              <p className="text-xs text-muted-foreground">Total Documentos</p>
            </div>
          </div>
        </div>
        <div className="card-premium p-4">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-full bg-blue-500/10 flex items-center justify-center">
              <FolderOpen className="h-5 w-5 text-blue-500" strokeWidth={1.5} />
            </div>
            <div>
              <p className="text-2xl font-light">{new Set(documents.map(d => d.category)).size}</p>
              <p className="text-xs text-muted-foreground">Categorías</p>
            </div>
          </div>
        </div>
        <div className="card-premium p-4">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-full bg-green-500/10 flex items-center justify-center">
              <Download className="h-5 w-5 text-green-500" strokeWidth={1.5} />
            </div>
            <div>
              <p className="text-2xl font-light">{totalDownloads}</p>
              <p className="text-xs text-muted-foreground">Descargas Totales</p>
            </div>
          </div>
        </div>
        <div className="card-premium p-4">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-full bg-purple-500/10 flex items-center justify-center">
              <File className="h-5 w-5 text-purple-500" strokeWidth={1.5} />
            </div>
            <div>
              <p className="text-2xl font-light">{formatSize(totalSize)}</p>
              <p className="text-xs text-muted-foreground">Almacenamiento</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-4 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Buscar documentos..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
            data-testid="search-documents"
          />
        </div>
        <Select value={categoryFilter} onValueChange={setCategoryFilter}>
          <SelectTrigger className="w-full md:w-[200px]" data-testid="filter-category">
            <Filter className="h-4 w-4 mr-2" />
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {categories.map(cat => (
              <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Documents List */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      ) : (
        <div className="card-premium divide-y divide-border">
          {filteredDocuments.length === 0 ? (
            <div className="p-8 text-center text-muted-foreground">
              <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>{documents.length === 0 ? 'No hay documentos. Sube el primero.' : 'No se encontraron documentos con ese filtro.'}</p>
            </div>
          ) : (
            filteredDocuments.map((doc) => (
              <div key={doc.document_id} className="p-3 lg:p-4 flex items-center gap-3 lg:gap-4 hover:bg-muted/30 transition-colors" data-testid={`doc-row-${doc.document_id}`}>
                <div className="flex-shrink-0 hidden sm:block">
                  {getFileIcon(doc.content_type, doc.original_filename)}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <p className="font-medium truncate text-sm">{doc.original_filename || doc.description}</p>
                    <Badge className={`${categoryColors[doc.category] || categoryColors.other} border-0 text-xs`}>
                      {categoryLabels[doc.category] || doc.category}
                    </Badge>
                  </div>
                  <div className="flex items-center gap-2 lg:gap-4 text-xs text-muted-foreground flex-wrap">
                    <span>{formatSize(doc.size)}</span>
                    <span className="hidden sm:inline">•</span>
                    <span className="hidden sm:inline">{doc.user_name || doc.uploaded_by || 'Admin'}</span>
                    <span>•</span>
                    <span>{formatDate(doc.created_at)}</span>
                  </div>
                </div>
                <div className="flex items-center gap-0.5 flex-shrink-0">
                  <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handlePreview(doc)} title="Previsualizar" data-testid={`preview-${doc.document_id}`}>
                    <Eye className="h-4 w-4" strokeWidth={1.5} />
                  </Button>
                  <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleDownload(doc)} title="Descargar" data-testid={`download-${doc.document_id}`}>
                    <Download className="h-4 w-4" strokeWidth={1.5} />
                  </Button>
                  <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => handleDelete(doc)} title="Eliminar" data-testid={`delete-${doc.document_id}`}>
                    <Trash2 className="h-4 w-4" strokeWidth={1.5} />
                  </Button>
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default AdminDocumentsPage;
